import { useCallback, useEffect, useState } from 'react';
import {
  createMultimediaRequest,
  deleteMultimediaRequest,
  listMultimediaRequest,
  updateMultimediaRequest
} from '@services/multimedia/multimediaApi';

// Normalizes possible multimedia response payloads into an array.
function extractMultimedia(body) {
  if (Array.isArray(body)) return body;
  if (Array.isArray(body?.multimediaList)) return body.multimediaList;
  if (Array.isArray(body?.multimedia)) return body.multimedia;
  if (body?.multimedia) return [body.multimedia];
  return [];
}

// CRUD hook for multimedia library screens.
function useMultimedia() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const fetchItems = useCallback(async () => {
    // Shared refresh used on mount and after create/update/delete.
    setError('');
    try {
      const response = await listMultimediaRequest();
      setItems(extractMultimedia(response.body));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  async function createItem(payload) {
    setSubmitting(true);
    setError('');
    setSuccess('');
    try {
      const response = await createMultimediaRequest(payload);
      setSuccess(response.message || 'Multimedia created.');
      await fetchItems();
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setSubmitting(false);
    }
  }

  async function updateItem(id, payload) {
    setSubmitting(true);
    setError('');
    setSuccess('');
    try {
      const response = await updateMultimediaRequest(id, payload);
      setSuccess(response.message || 'Multimedia updated.');
      await fetchItems();
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setSubmitting(false);
    }
  }

  async function removeItem(id) {
    setSubmitting(true);
    setError('');
    setSuccess('');
    try {
      const response = await deleteMultimediaRequest(id);
      setSuccess(response.message || 'Multimedia deleted.');
      await fetchItems();
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setSubmitting(false);
    }
  }

  return {
    items,
    loading,
    submitting,
    error,
    success,
    createItem,
    updateItem,
    removeItem
  };
}

export default useMultimedia;
